import React, { ReactNode } from 'react';

import styled from 'styled-components';

// theme
import aampeTheme from '../../../constants/aampeTheme';

import { formatLastUpdatedDate } from '../../../helpers/dateTimeHelpers';

// models
import { FormulaTypesData } from '../../../models/formula.model';

interface ChannelData {
    count: number;
    sum: number;
    lastUpdated?: Date | undefined;
}

interface ProcessedData {
    channel: ReactNode;
    messages: number;
    uniqueMessages: number;
    lastUpdated?: string;
    messageType: string;
}

export const processData = (
    tableData: Array<any>,
    options: { isDraft?: boolean } = {},
): ProcessedData[] => {
    if (!tableData || !Array.isArray(tableData)) {
        return [];
    }

    const channelData: Record<string, ChannelData> = {};

    for (const item of tableData) {
        const { message_type, message_count = 0, updated_at } = item;

        const lastUpdated: Date | undefined =
            options.isDraft && updated_at ? new Date(updated_at) : undefined;

        channelData[message_type] = channelData[message_type] || {
            count: 0,
            sum: 0,
        };

        channelData[message_type].count += 1;
        channelData[message_type].sum += message_count;

        const channelLastUpdated =
            channelData[message_type]?.lastUpdated ?? undefined;

        if (
            lastUpdated &&
            (!channelLastUpdated || lastUpdated > channelLastUpdated)
        ) {
            channelData[message_type].lastUpdated = lastUpdated;
        }
    }

    return Object.entries(channelData)
        .map(([channel, data]) => {
            const formulaTypeData =
                FormulaTypesData[channel as keyof typeof FormulaTypesData];

            if (formulaTypeData) {
                const { title: displayName, Icon } = formulaTypeData;
                const lastUpdatedString = formatLastUpdatedDate(
                    data.lastUpdated,
                );

                return {
                    channel: (
                        <IconandIndicatorWrapper>
                            <Indicator
                                backgroundColor={
                                    formulaTypeData.color ||
                                    aampeTheme.colors.white
                                }
                            />
                            {Icon && <Icon />}
                            <ChannelName>{displayName}</ChannelName>
                        </IconandIndicatorWrapper>
                    ),
                    messages: data.count,
                    messageType: channel,
                    uniqueMessages: data.sum ?? 0,
                    lastUpdated: options.isDraft
                        ? lastUpdatedString
                        : undefined,
                };
            }

            return null;
        })
        .filter(Boolean) as ProcessedData[];
};
const Indicator = styled.div<{ backgroundColor: string }>(
    ({ backgroundColor }) => ({
        width: '8px',
        height: '8px',
        borderRadius: aampeTheme.borderRadius.default,
        backgroundColor: backgroundColor,
        marginRight: aampeTheme.spacing(1),
    }),
);
const ChannelName = styled.div({
    marginLeft: aampeTheme.spacing(1),
});

const IconandIndicatorWrapper = styled.div({
    display: 'flex',
    alignItems: 'center',
});
